import api from "../config/api";

import {
  ServicePaginatedReturn,
  ServiceReturn,
} from "../types/ServiceResponse";

import { Message } from "../models/Message";
import { Chat } from "../models/Chat";
import { Service } from "../models/Service";
import { normalizeMessage, normalizeService } from "./messageAdapters";
import { normalizeChannelConnection } from "../models/Channel";
import { ForwardMessageResult, ForwardRecipient } from "../models/ForwardRecipient";

const moduleBaseUrl = "chat";

export interface GetMessagesByChatPayload {
  page?: number;
  perPage?: number;
  chatId?: string;
  beforeCreatedAt?: string;
  beforeId?: string;
};

export interface GetMessageContextPayload {
  chatId: string;
  messageId: string;
  perPage?: number;
};

export interface GetConversationsPayload {
  order?: string;
  page?: number;
  perPage?: number;
  search?: string;
  queueId?: string;
  channelId?: string;
};

export interface MessageContext {
  before: Array<Message>;
  target: Message;
  after: Array<Message>;
};

export interface GetForwardRecipientsPayload {
  chatId: string;
  search?: string;
  page?: number;
  perPage?: number;
};

export interface ForwardMessagePayload {
  chatId: string;
  messageId: string;
  requestId: string;
  contactIds: Array<string>;
};

export const deduplicateContactIDs = ( contactIds: Array<string> ): Array<string> => {
  const seen = new Set<string>();

  return contactIds.reduce(( result, contactId ) => {
    const normalizedID = contactId.trim();
    if( !normalizedID || seen.has( normalizedID )) return result;
    seen.add( normalizedID );
    result.push( normalizedID );
    return result;
  }, [] as Array<string>);
};

export const getForwardRecipients = async ( payload: GetForwardRecipientsPayload, token?: string ): Promise<ServicePaginatedReturn<ForwardRecipient>> => {
  try {
    const { chatId, ...params } = payload;
    const { data } = await api.get(`/${ moduleBaseUrl }/${ chatId }/forward-recipients`, {
      headers: { Authorization: `Bearer ${ token }` },
      params,
    });

    return data;
  } catch ( error: any ) {
    return { data: false, message: error?.response?.data?.message ?? error?.message };
  }
};

export const forwardMessage = async ( payload: ForwardMessagePayload, token?: string ): Promise<ServiceReturn<{ results: Array<ForwardMessageResult> }>> => {
  try {
    const { chatId, messageId, requestId, contactIds } = payload;
    const { data } = await api.post(`/${ moduleBaseUrl }/${ chatId }/message/${ messageId }/forward`, {
      requestId,
      contactIds: deduplicateContactIDs( contactIds ),
    }, {
      headers: { Authorization: `Bearer ${ token }` },
    });

    return data;
  } catch ( error: any ) {
    return { data: false, message: error?.response?.data?.message ?? error?.message };
  }
};

export const getMessagesByChat = async ( params: GetMessagesByChatPayload, token?: string ): Promise<ServicePaginatedReturn<Message>> => {
  try {
    const {
      data
    } = await api.get(`/${ moduleBaseUrl }/messages`, {
      headers: {
        Authorization: `Bearer ${ token }`,
      },
      params,
    });

    if( data?.data?.results ) {
      data.data.results = data.data.results.map( normalizeMessage );
    }

    return data;
  } catch ( error: any ) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const getConversations = async ( params: GetConversationsPayload, token?: string ): Promise<ServicePaginatedReturn<Chat>> => {
  try {
    const {
      data
    } = await api.get(`/${ moduleBaseUrl }`, {
      headers: {
        Authorization: `Bearer ${ token }`,
      },
      params,
    });

    if( data?.data?.results ) {
      data.data.results = data.data.results.map(( chat: Chat ) => {
        const channel = chat.channel ? normalizeChannelConnection( chat.channel ) : chat.channel;

        return {
          ...chat,
          createdAt: new Date( chat.createdAt ),
          updatedAt: new Date( chat.updatedAt ),
          deletedAt: chat.deletedAt ? new Date( chat.deletedAt ) : chat.deletedAt,
          channel,
          messages: chat.messages?.map(( message ) => normalizeMessage( message )) ?? [],
          services: chat.services?.map(( service ) => normalizeService( service )) ?? [],
        };
      });
    }

    return data;
  } catch ( error: any ) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const getMessageContext = async ( params: GetMessageContextPayload, token?: string ): Promise<ServiceReturn<MessageContext>> => {
  try {
    const {
      data
    } = await api.get(`/${ moduleBaseUrl }/messages/context`, {
      headers: {
        Authorization: `Bearer ${ token }`,
      },
      params,
    });

    if( data?.data ) {
      data.data.before = data.data.before?.map( normalizeMessage ) ?? [];
      data.data.target = normalizeMessage( data.data.target );
      data.data.after = data.data.after?.map( normalizeMessage ) ?? [];
    }

    return data;
  } catch ( error: any ) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const getServiceByChatId = async ( id: string, token?: string ): Promise<ServiceReturn<Service>> => {
  try {
    const {
      data
    } = await api.get(`/${ moduleBaseUrl }/${ id }/service`, {
      headers: {
        Authorization: `Bearer ${ token }`,
      },
    });

    return data;
  } catch ( error: any ) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const markChatRead = async ( id: string, token?: string ): Promise<ServiceReturn<boolean>> => {
  try {
    const {
      data
    } = await api.post(`/${ moduleBaseUrl }/${ id }/read`, {}, {
      headers: {
        Authorization: `Bearer ${ token }`,
      },
    });

    return data;
  } catch ( error: any ) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};
